// src/v3/Button/index.tsx
import { forwardRef, useContext } from "react";
import { cx, cva } from "class-variance-authority";

// src/contexts/ComponentProvider.tsx
import { createContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var GLOBAL_KEY = "__@jouzen/ecom-components:ComponentContext__";
function getOrCreateContext() {
  const g = globalThis;
  if (!g[GLOBAL_KEY])
    g[GLOBAL_KEY] = createContext({});
  return g[GLOBAL_KEY];
}
var Context = getOrCreateContext();
function ComponentProvider({
  children,
  LinkComponent,
  ImageComponent
}) {
  const contextValue = useMemo(
    () => ({ ImageComponent, LinkComponent }),
    [ImageComponent, LinkComponent]
  );
  return /* @__PURE__ */ jsx(Context.Provider, {
    value: contextValue,
    children
  });
}
ComponentProvider.displayName = "ComponentProvider";

// src/v3/CircularProgress/index.tsx
import { motion } from "framer-motion";
import { jsx as jsx2 } from "react/jsx-runtime";
var SIZE = 44;
function CircularProgress({
  size = 40,
  thickness = 3.6,
  ...props
}) {
  return /* @__PURE__ */ jsx2("span", {
    ...props,
    "data-chromatic": "ignore",
    children: /* @__PURE__ */ jsx2(motion.span, {
      animate: { rotate: 360 },
      className: "ui-block",
      initial: { rotate: 0 },
      role: "progressbar",
      style: { width: size, height: size },
      transition: {
        repeat: Infinity,
        ease: "linear",
        duration: 1.4
      },
      children: /* @__PURE__ */ jsx2("svg", {
        className: "ui-block",
        viewBox: `${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`,
        children: /* @__PURE__ */ jsx2(motion.circle, {
          animate: {
            strokeDasharray: ["1, 200", "100, 200", "100, 200"],
            strokeDashoffset: [0, -15, -125]
          },
          cx: SIZE,
          cy: SIZE,
          fill: "none",
          initial: {
            strokeDasharray: "80px, 200px",
            strokeDashoffset: 0
          },
          r: (SIZE - thickness) / 2,
          stroke: "currentColor",
          strokeDasharray: "80px, 200px",
          strokeDashoffset: 0,
          strokeWidth: thickness,
          transition: { ease: "easeInOut", repeat: Infinity, duration: 1.4 }
        })
      })
    })
  });
}
CircularProgress.displayName = "CircularProgress";

// src/icons/CardBtnArrowIcon.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
function CardBtnArrowIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 13,
    viewBox: "0 0 13 13",
    width: 13,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx3("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx3("path", {
        d: "M12.5303 6.53033C12.8232 6.23744 12.8232 5.76256 12.5303 5.46967L7.75736 0.696699C7.46447 0.403806 6.98959 0.403806 6.6967 0.696699C6.40381 0.989593 6.40381 1.46447 6.6967 1.75736L10.9393 6L6.6967 10.2426C6.40381 10.5355 6.40381 11.0104 6.6967 11.3033C6.98959 11.5962 7.46447 11.5962 7.75736 11.3033L12.5303 6.53033ZM0 6.75L12 6.75V5.25L0 5.25L0 6.75Z",
        fill: "currentColor"
      })
    ]
  });
}

// src/icons/TextLinkRightArrowIcon.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
function TextLinkRightArrowIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs2("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 6,
    viewBox: "0 0 20 6",
    width: 20,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx4("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx4("path", {
        d: "M20 3L15 0.11325L15 5.88675L20 3ZM-4.37114e-08 3.5L15.5 3.5L15.5 2.5L4.37114e-08 2.5L-4.37114e-08 3.5Z",
        fill: "currentColor"
      })
    ]
  });
}

// src/v3/Button/index.tsx
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var buttonVar = cva(
  "ui-rounded-full ui-text-center ui-inline-block ui-transition",
  {
    variants: {
      variant: {
        primary: "ui-bg-blue-100 ui-text-sandstone-200 hover:ui-bg-blue-200",
        "secondary-light": "ui-bg-sandstone-200 ui-text-sandstone-500 hover:ui-bg-gray-400",
        "secondary-dark": "ui-bg-sandstone-500 ui-text-sandstone-200 hover:ui-bg-gray-600",
        "primary-text-link": "ui-text-stroke-color-transparent ui-text-stroke-width-0.5 hover:ui-text-stroke-color-current",
        "secondary-text-link": "ui-text-sandstone-200 ui-text-stroke-color-transparent ui-text-stroke-width-0.5 hover:ui-text-stroke-color-current",
        "outlined-dark": "ui-bg-transparent ui-text-sandstone-500 ui-border ui-border-solid ui-border-current",
        "outlined-light": "ui-bg-transparent ui-text-sandstone-200 ui-border ui-border-solid ui-border-current",
        unset: ""
      },
      size: {
        none: "ui-p-0",
        small: "ui-py-2 ui-px-4",
        medium: "ui-py-2 ui-px-4 lg:ui-py-3 lg:ui-px-6",
        large: "ui-py-3 ui-px-6"
      },
      loadingBtnPrimary: {
        true: "ui-bg-blue-200 ui-text-sandstone-200"
      },
      loadingBtnSecondaryLight: {
        true: "ui-bg-gray-400"
      },
      loadingBtnSecondaryDark: {
        true: "ui-bg-gray-600 ui-text-sandstone-200"
      },
      disabled: {
        true: "ui-pointer-events-none"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "large"
    },
    compoundVariants: [
      {
        class: "ui-bg-gray-300",
        disabled: true,
        variant: "primary"
      },
      {
        class: "ui-bg-gray-300 ui-text-sandstone-200",
        disabled: true,
        variant: "secondary-light"
      },
      {
        class: "ui-bg-gray-300 ui-text-sandstone-200",
        disabled: true,
        variant: "secondary-dark"
      },
      {
        class: "ui-text-gray-300",
        disabled: true,
        variant: "primary-text-link"
      },
      {
        class: "ui-text-gray-300",
        disabled: true,
        variant: "secondary-text-link"
      }
    ]
  }
);
function Spinner() {
  return /* @__PURE__ */ jsx5(CircularProgress, {
    "data-cy": "button-loading",
    size: 12,
    thickness: 3
  });
}
function ButtonCompartment({
  children,
  className = "",
  external,
  loading,
  icon,
  isTextLink
}) {
  const showCardButtonArrow = external && !isTextLink;
  const showTextLinkRightArrow = !external && isTextLink;
  const showIcon = Boolean(icon) && !showCardButtonArrow && !showTextLinkRightArrow;
  return /* @__PURE__ */ jsxs3("span", {
    className: cx(
      "ui-items-center ui-inline-flex ui-gap-2 ui-content-center ui-justify-center",
      className,
      {
        "ui-relative": loading,
        "ui-underline": isTextLink
      }
    ),
    "data-cy": "button-compartment",
    children: [
      /* @__PURE__ */ jsx5("span", {
        children
      }),
      showCardButtonArrow ? /* @__PURE__ */ jsx5(CardBtnArrowIcon, {
        "aria-hidden": true,
        className: "ui--rotate-45",
        "data-cy": "card_button_arrow_icon"
      }) : null,
      showTextLinkRightArrow ? /* @__PURE__ */ jsx5(TextLinkRightArrowIcon, {
        "aria-hidden": true,
        "data-cy": "link_button_arrow_icon"
      }) : null,
      showIcon ? icon : null
    ]
  });
}
var Button = forwardRef(function Button2({
  buttonCompartmentClassName = "",
  children,
  className = "",
  disabled = false,
  external,
  href = "",
  icon,
  loading = false,
  loadingTextEnabled = true,
  size,
  type = "button",
  variant = "primary",
  ...props
}, ref) {
  const context = useContext(Context);
  const LinkComponent = external || !context.LinkComponent ? "a" : context.LinkComponent;
  const isTextLink = variant === "primary-text-link" || variant === "secondary-text-link";
  const _size = isTextLink ? "none" : size;
  const loadingBtnPrimary = variant === "primary" && loading;
  const loadingBtnSecondaryLight = variant === "secondary-light" && loading;
  const loadingBtnSecondaryDark = variant === "secondary-dark" && loading;
  if (href) {
    return /* @__PURE__ */ jsx5(LinkComponent, {
      "aria-disabled": disabled,
      className: buttonVar({
        variant,
        size: _size,
        disabled,
        className
      }),
      "data-cy": "button-link",
      href,
      ref,
      ...props,
      children: /* @__PURE__ */ jsx5(ButtonCompartment, {
        className: buttonCompartmentClassName,
        external,
        icon,
        isTextLink,
        children
      })
    });
  } else if (loadingTextEnabled && loading) {
    return /* @__PURE__ */ jsx5("button", {
      className: buttonVar({
        variant: "unset",
        size: _size,
        loadingBtnPrimary,
        loadingBtnSecondaryLight,
        loadingBtnSecondaryDark,
        disabled,
        className
      }),
      "data-cy": "button-loading-text",
      disabled: true,
      ref,
      type,
      ...props,
      children: /* @__PURE__ */ jsxs3("div", {
        className: "ui-inline-flex ui-gap-2 ui-justify-center ui-items-center",
        children: [
          /* @__PURE__ */ jsx5(Spinner, {}),
          children,
          isTextLink ? /* @__PURE__ */ jsx5(TextLinkRightArrowIcon, {}) : null
        ]
      })
    });
  }
  return /* @__PURE__ */ jsx5("button", {
    className: buttonVar({
      variant: loading ? "unset" : variant,
      size: _size,
      loadingBtnPrimary,
      loadingBtnSecondaryLight,
      loadingBtnSecondaryDark,
      disabled,
      className
    }),
    "data-cy": "button",
    disabled: loading || disabled,
    ref,
    type,
    ...props,
    children: /* @__PURE__ */ jsxs3(ButtonCompartment, {
      className: buttonCompartmentClassName,
      icon,
      isTextLink,
      loading,
      children: [
        /* @__PURE__ */ jsx5("span", {
          className: cx({ "ui-invisible": loading }),
          children
        }),
        loading ? /* @__PURE__ */ jsx5("span", {
          className: "ui-m-0 ui-absolute ui-top-0 ui-left-0 ui-right-0 ui-bottom-0 ui-flex ui-justify-center ui-items-center",
          children: /* @__PURE__ */ jsx5(Spinner, {})
        }) : null
      ]
    })
  });
});
Button.displayName = "Button";
export {
  Button
};
//# sourceMappingURL=index.mjs.map